import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { startTransformerExport } from '~/features/transformers/services/transformer-export.service';
import {
  transformerErrorCode,
  transformerErrorMessage,
} from '~/features/transformers/utils/transformer-errors';
import { extractApiError } from '~/utils/extract-api-error';

import {
  cancelExport,
  deleteExport,
  estimateExport,
  getExportConfig,
  listExports,
  triggerExport,
} from '../services/export.service';
import { useExportStore } from '../store/export.store';
import type {
  ExportConnector,
  ExportLayout,
  ExportStatus,
  ExportTriggerRequest,
  ExportTriggerResponse,
} from '../types/export.types';

/** Stable Sonner toast id for an export — the runner re-emits this toast in place. */
function toastIdFor(exportId: string): string {
  return `export-${exportId}`;
}

/** Identical estimate requests within this window are answered from the cache. */
const ESTIMATE_REUSE_MS = 60 * 1000;

/** Dry-run estimate for the pre-trigger confirmation dialog. Still a mutation for its callers
 *  (they fire it on a debounced change), but routed through the query cache keyed on the
 *  request — toggling a filter off and back on reuses the answer instead of asking again. */
export function useEstimateExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ExportTriggerRequest) =>
      queryClient.fetchQuery({
        queryKey: [...queryKeys.exports.all, 'estimate', request],
        queryFn: () => estimateExport(request),
        staleTime: ESTIMATE_REUSE_MS,
      }),
  });
}

interface TriggerVars {
  request: ExportTriggerRequest;
  /** Human context shown in the toast (experiment / batch name). */
  label: string;
  connector: ExportConnector;
  /**
   * A file layout is in force: start the export on integrations
   * (`POST /v1/transformer-exports`) instead of the standard trigger. `request`
   * still carries the standard body, used as-is if integrations answers 422
   * `NO_TRANSFORMER` (nothing assigned after all).
   */
  layout?: ExportLayout;
}

/** The trigger result plus whether it went through a file transformer. */
type TriggerResult = ExportTriggerResponse & { transformed: boolean };

/**
 * Start a transformed export on integrations and map its `TransformerRun` onto the
 * trigger response shape the store tracks. Returns null on 422 `NO_TRANSFORMER`
 * for a resolved (not explicitly picked) layout, so the caller falls back to the
 * standard export.
 */
async function triggerTransformedExport(
  request: ExportTriggerRequest,
  connector: ExportConnector,
  layout: ExportLayout,
): Promise<TriggerResult | null> {
  try {
    const run = await startTransformerExport({
      subsidiaryId: request.tenantContext.subsidiaryId,
      connector,
      sourceWorkflowId: request.sourceWorkflowId,
      ...(layout.transformerId ? { transformerId: layout.transformerId } : {}),
      ...(layout.transformerVersion != null
        ? { transformerVersion: layout.transformerVersion }
        : {}),
      ...(request.filters ? { filters: { ...request.filters } } : {}),
    });
    // integrations answers 202 only once export-service accepted the job (a trigger
    // failure is an error response), so `exportId` is always set here. Defensive guard
    // against a contract break only — it surfaces through the generic error toast.
    if (!run.exportId)
      throw new Error(run.error ?? 'Transformer export accepted without an export id');
    return {
      exportId: run.exportId,
      workflowId: run.id,
      runId: '',
      status: 'PENDING',
      transformed: true,
    };
  } catch (err) {
    if (!layout.transformerId && transformerErrorCode(err) === 'NO_TRANSFORMER') return null;
    throw err;
  }
}

/**
 * Trigger an export and register it with the global store so the app-level
 * manager starts streaming progress.
 *
 * On a 409 (an identical export is already running) it adopts the existing
 * in-flight export instead of erroring — but only when no template was asked
 * for. The server's duplicate guard hashes the run and its filters, NOT the
 * template, so a request for a feed encoding can collide with a running raw
 * export; adopting that one would report success and hand the user unencoded
 * data. When a template was named we surface the conflict instead.
 */
export function useTriggerExport() {
  const startExport = useExportStore((s) => s.startExport);
  const { t } = useTranslation('exports');

  return useMutation({
    mutationFn: async ({ request, connector, layout }: TriggerVars): Promise<TriggerResult> => {
      if (layout) {
        const transformed = await triggerTransformedExport(request, connector, layout);
        if (transformed) return transformed;
      }
      try {
        return { ...(await triggerExport(request)), transformed: false };
      } catch (err) {
        // Dedup: an identical export (same source + filters) is already running.
        // Adopt it by looking up the subsidiary's in-flight export for this run.
        if (err instanceof ApiError && err.statusCode === 409 && !request.template) {
          const running = await findRunningExport(request.sourceWorkflowId);
          if (running) {
            return {
              exportId: running.exportId,
              workflowId: running.workflowId,
              runId: '',
              status: 'IN_PROGRESS',
              transformed: false,
            };
          }
        }
        throw err;
      }
    },
    onSuccess: (res, { request, label, connector, layout }) => {
      startExport({
        exportId: res.exportId,
        workflowId: res.workflowId,
        sourceWorkflowId: request.sourceWorkflowId,
        label,
        connector,
        tenantContext: request.tenantContext,
        ...(request.filters ? { filters: request.filters } : {}),
        // A transformed export retries through integrations with the same pick; the
        // standard template doesn't apply to it.
        ...(res.transformed && layout ? { layout } : {}),
        ...(!res.transformed && request.template ? { template: request.template } : {}),
        toastId: toastIdFor(res.exportId),
        latest: null,
        startedAt: Date.now(),
      });
    },
    onError: (err, { request, layout }) => {
      if (layout) {
        toast.error(transformerErrorMessage(err, t('toast.triggerError')));
        return;
      }
      // A 409 that reached here is the template case above: an export for this
      // run is already in flight and we cannot tell which template it used, so
      // say that rather than reporting a generic failure.
      if (err instanceof ApiError && err.statusCode === 409 && request.template) {
        toast.error(t('toast.templateConflict'));
        return;
      }
      toast.error(extractApiError(err, t('toast.triggerError')));
    },
  });
}

/** Find a still-running (PENDING/IN_PROGRESS) export for a source run, newest first. */
async function findRunningExport(sourceWorkflowId: string) {
  const running = await listExports({ sourceWorkflowId });
  return running.find((e) => e.status === 'IN_PROGRESS' || e.status === 'PENDING') ?? null;
}

/** Cancel a running export. The row transitions to CANCELLED via a later SSE frame. */
export function useCancelExport() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('exports');
  return useMutation({
    mutationFn: (exportId: string) => cancelExport(exportId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.exports.all });
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('toast.cancelError')));
    },
  });
}

/** Delete a terminal export (DB row + GCS blob) from the history view. */
export function useDeleteExport() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('exports');
  return useMutation({
    mutationFn: (exportId: string) => deleteExport(exportId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.exports.all });
    },
    onError: (err) => {
      toast.error(extractApiError(err, t('toast.deleteError')));
    },
  });
}

/**
 * The configuration an export ran with (Recent exports' "Configuration"), fetched only when
 * opened. Immutable once written, so cached for the session; `null` = none recorded.
 */
export function useExportConfig(exportId: string) {
  return useQuery({
    queryKey: queryKeys.exports.config(exportId),
    queryFn: () => getExportConfig(exportId),
    staleTime: Infinity,
    retry: false,
  });
}

/** A created-at window for the history list: ISO instants, `from` inclusive, `to` exclusive. */
export interface ExportCreatedWindow {
  from?: string;
  to?: string;
}

/** The most rows a windowed history read asks for (the server's cap). */
export const WINDOWED_LIST_LIMIT = 100;

/** List the subsidiary's exports for a source run (drives the history sheet), optionally
 *  within a created-at window — the server filters before its limit, so a window is exact. */
export function useExportsList(
  sourceWorkflowId?: string,
  status?: ExportStatus,
  enabled = true,
  window: ExportCreatedWindow = {},
) {
  const windowed = Boolean(window.from ?? window.to);
  return useQuery({
    queryKey: queryKeys.exports.list(sourceWorkflowId, status, window.from, window.to),
    queryFn: () =>
      listExports({
        ...(sourceWorkflowId ? { sourceWorkflowId } : {}),
        ...(status ? { status } : {}),
        ...(window.from ? { createdFrom: window.from } : {}),
        ...(window.to ? { createdTo: window.to } : {}),
        ...(windowed ? { limit: WINDOWED_LIST_LIMIT } : {}),
      }),
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}
